import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, MessageCircle, Calendar, Download, ExternalLink, 
  User as UserIcon, Share2, Edit, Mail, Phone, Globe, 
  Instagram, Facebook, Linkedin, Twitter, Wifi, QrCode,
  Copy, CheckCircle2
} from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';
import { downloadUserVCard } from '../lib/contactDownload';
import ContactDownloadModal from '../components/ContactDownloadModal';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to ensure URL has protocol
const ensureProtocol = (url) => {
  if (!url) return url;
  // If URL already has protocol, return as is
  if (url.match(/^https?:\/\//i)) {
    return url;
  }
  // Add https:// if missing
  return `https://${url}`;
};

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentUser = getUser();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [nfcSupported, setNfcSupported] = useState(false);
  const [writingNFC, setWritingNFC] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const isOwnProfile = currentUser?.id === userId;

  useEffect(() => {
    loadUserProfile();
    loadUserPosts();
    loadQRCode();
    checkNFCSupport();
  }, [userId]);

  const checkNFCSupport = () => {
    if ('NDEFReader' in window) {
      setNfcSupported(true);
    }
  };

  const handleWriteToNFC = async () => {
    if (!nfcSupported) {
      toast.error('NFC not supported on this device');
      return;
    }

    try {
      setWritingNFC(true);
      const ndef = new window.NDEFReader();
      
      // Create vCard data
      const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name || ''}
ORG:${user.businessName || ''}
TEL:${user.phone || ''}
EMAIL:${user.email || ''}
URL:${window.location.origin}/profile/${userId}
END:VCARD`;

      await ndef.write({
        records: [
          { recordType: "text", data: vCardData },
          { recordType: "url", data: `${window.location.origin}/profile/${userId}` }
        ]
      });

      toast.success('✅ NFC Card Written Successfully! Tap your card to the device.');
      setWritingNFC(false);
    } catch (error) {
      console.error('NFC write error:', error);
      toast.error('Failed to write NFC. Make sure NFC is enabled and card is near device.');
      setWritingNFC(false);
    }
  };

  const handleCopyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied!`);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handleShareMeetingLink = () => {
    const meetingLink = `${window.location.origin}/schedule/${userId}`;
    navigator.clipboard.writeText(meetingLink);
    toast.success('Meeting schedule link copied to clipboard!');
  };

  const handleSaveAndDownloadVCard = async (visitorInfo = null) => {
    try {
      // Check if user is logged in
      if (!currentUser && !visitorInfo) {
        // Show modal for non-logged-in users
        setShowDownloadModal(true);
        return;
      }

      // Download vCard with tracking
      await downloadUserVCard(userId, 'profile', null, visitorInfo);
      
      // Save to My Connections (localStorage)
      const existingContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      
      const existingIndex = existingContacts.findIndex(c => 
        c.email === user?.email || (c.name === user?.name && c.id === userId)
      );
      
      const contactData = {
        id: existingIndex >= 0 ? existingContacts[existingIndex].id : Date.now().toString(),
        name: user?.name || '',
        businessName: user?.businessName || '',
        title: '',
        email: user?.email || '',
        phone: '',
        mobile: '',
        website: '',
        address: user?.address || '',
        linkedin: '',
        scannedAt: new Date().toISOString(),
        source: isOwnProfile ? 'my-card' : 'profile',
      };
      
      if (existingIndex >= 0) {
        existingContacts[existingIndex] = contactData;
      } else {
        existingContacts.unshift(contactData);
      }
      
      const trimmedContacts = existingContacts.slice(0, 100);
      localStorage.setItem('scannedContacts', JSON.stringify(trimmedContacts));
      
      toast.success('✅ vCard downloaded & saved to My Connections!');
    } catch (error) {
      console.error('vCard error:', error);
      toast.error('Failed to download vCard');
    }
  };

  const handleModalSubmit = async (visitorInfo) => {
    await handleSaveAndDownloadVCard(visitorInfo);
  };

  const handleDownloadQR = () => {
    if (!qrCode) {
      toast.error('QR code not available');
      return;
    }
    
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `${user?.name || 'user'}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded!');
  };

  const fixImageUrl = (url) => {
    if (!url) return url;
    
    // If URL is from ANY old preview domain, replace with current backend URL
    if (url.includes('.preview.emergentagent.com/api/')) {
      const path = url.split('/api/')[1];
      if (path) {
        return `${process.env.REACT_APP_BACKEND_URL}/api/${path}`;
      }
    }
    
    // If URL is relative, prepend backend URL
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    
    return url;
  };

  const loadUserProfile = async () => {
    try {
      // Profile endpoint is public - no auth required
      const response = await axios.get(`${API}/users/${userId}`);
      const userData = response.data;
      
      // Fix image URLs
      if (userData.profilePicUrl) {
        userData.profilePicUrl = fixImageUrl(userData.profilePicUrl);
      }
      if (userData.logoUrl) {
        userData.logoUrl = fixImageUrl(userData.logoUrl);
      }
      
      setUser(userData);
    } catch (error) {
      toast.error('Failed to load profile');
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async () => {
    try {
      // Try to load posts - may fail if not authenticated but that's okay
      const config = currentUser ? { headers: getAuthHeaders() } : {};
      const response = await axios.get(`${API}/community/user/${userId}`, config);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to load posts (may require auth)');
      // Don't show error - posts are optional for public viewing
    }
  };

  const loadQRCode = async () => {
    try {
      // QR code endpoint is public, no auth needed
      const response = await axios.get(`${API}/users/${userId}/qr`);
      setQrCode(response.data.dataUrl);
    } catch (error) {
      console.error('Failed to load QR code:', error);
      toast.error('Failed to load QR code');
    }
  };

  const getSocialIcon = (platform) => {
    const platformLower = platform?.toLowerCase() || '';
    if (platformLower.includes('instagram')) return <Instagram className="w-5 h-5" />;
    if (platformLower.includes('facebook')) return <Facebook className="w-5 h-5" />;
    if (platformLower.includes('linkedin')) return <Linkedin className="w-5 h-5" />;
    if (platformLower.includes('twitter') || platformLower.includes('x.com')) return <Twitter className="w-5 h-5" />;
    return <ExternalLink className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/browse')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Browse
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Modern Business Card - Left Column */}
          <div className="lg:col-span-1">
            {/* Digital Business Card */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
              {/* Profile Picture - Full Width with Wave */}
              <div className="relative h-48 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500">
                {user.profilePicUrl ? (
                  <img
                    src={user.profilePicUrl}
                    alt={user.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Profile picture failed to load:', user.profilePicUrl);
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-24 h-24 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <UserIcon className="w-24 h-24 text-white opacity-50" />
                  </div>
                )}
                {/* Wave Separator - Flipped Upside Down */}
                <div className="absolute bottom-0 left-0 right-0">
                  <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12" style={{ transform: 'scaleY(-1)' }}>
                    <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" 
                          className="fill-slate-800"></path>
                  </svg>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Name and Business */}
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    {user.name}
                  </h1>
                  {user.businessName && (
                    <p className="text-cyan-400 text-lg font-semibold">{user.businessName}</p>
                  )}
                  {user.businessCategory && (
                    <p className="text-slate-400 text-sm mt-1">{user.businessCategory}</p>
                  )}
                </div>

                {/* Contact Information */}
                <div className="space-y-3 mb-6">
                  {user.phone && (
                    <a 
                      href={`tel:${user.phone}`}
                      className="flex items-start gap-3 group cursor-pointer hover:bg-slate-800/50 p-3 rounded-lg transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-xs">Phone</p>
                        <p className="text-slate-200 text-sm font-medium group-hover:text-green-400 transition-colors">{user.phone}</p>
                      </div>
                    </a>
                  )}

                  {user.email && (
                    <a
                      href={`mailto:${user.email}`}
                      className="flex items-start gap-3 group cursor-pointer hover:bg-slate-800/50 p-3 rounded-lg transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-xs">Email</p>
                        <p className="text-slate-200 text-sm break-all font-medium group-hover:text-blue-400 transition-colors">{user.email}</p>
                      </div>
                    </a>
                  )}

                  {user.website && (
                    <a 
                      href={ensureProtocol(user.website)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group cursor-pointer hover:bg-slate-800/50 p-3 rounded-lg transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-xs">Website</p>
                        <p className="text-slate-200 text-sm break-all font-medium group-hover:text-cyan-400 transition-colors">
                          {user.website}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-cyan-400 flex-shrink-0 mt-1" />
                    </a>
                  )}

                  {/* Social Links */}
                  {user.socialLinks && user.socialLinks.length > 0 && (
                    <>
                      {user.socialLinks.map((link, idx) => (
                        <a 
                          key={idx}
                          href={ensureProtocol(link.url)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-start gap-3 group cursor-pointer hover:bg-slate-800/50 p-3 rounded-lg transition-colors"
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            {getSocialIcon(link.platform)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-400 text-xs">{link.platform}</p>
                            <p className="text-slate-200 text-sm break-all font-medium group-hover:text-cyan-400 transition-colors">
                              {link.url}
                            </p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-cyan-400 flex-shrink-0 mt-1" />
                        </a>
                      ))}
                    </>
                  )}
                </div>

                {/* About/Description */}
                {user.about && (
                  <div className="mb-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                    <p className="text-slate-300 text-sm leading-relaxed">{user.about}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  {isOwnProfile ? (
                    <>
                      <Button
                        onClick={() => navigate('/profile/edit')}
                        className="w-full bg-slate-700 hover:bg-slate-600 text-white rounded-xl py-6 font-semibold text-lg"
                      >
                        <Edit className="w-5 h-5 mr-2" />
                        EDIT
                      </Button>
                      <Button
                        onClick={handleSaveAndDownloadVCard}
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white rounded-xl py-6 font-semibold text-lg shadow-lg shadow-cyan-500/50"
                      >
                        <Share2 className="w-5 h-5 mr-2" />
                        SHARE
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => navigate(`/chat/${userId}`)}
                        className="w-full bg-slate-700 hover:bg-slate-600 text-white rounded-xl py-6 font-semibold"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Message
                      </Button>
                      <Button
                        onClick={handleSaveAndDownloadVCard}
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white rounded-xl py-6 font-semibold shadow-lg shadow-cyan-500/50"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Save Contact
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* QR Code Card - Visible to Everyone */}
            {qrCode && (
              <div className="mt-6 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl shadow-2xl p-6 border border-blue-500">
                <h3 className="text-xl font-bold text-white mb-4 text-center" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  {isOwnProfile ? 'Share Your Digital Card' : 'Scan to Save Contact'}
                </h3>

                {/* QR Code */}
                <div className="mb-4">
                  <div className="bg-white p-4 rounded-xl mx-auto" style={{maxWidth: '280px'}}>
                    <img
                      src={qrCode}
                      alt="QR Code"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-white text-center text-sm mt-3 opacity-90">
                    {isOwnProfile 
                      ? 'Share this QR code to instantly connect' 
                      : 'Scan with your phone to add this contact'}
                  </p>
                </div>

                <Button
                  onClick={handleDownloadQR}
                  className="w-full bg-white text-blue-600 hover:bg-blue-50 rounded-xl font-semibold py-3"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            )}

            {/* NFC & Share Card - Own Profile Only */}
            {isOwnProfile && (
              <div className="mt-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl p-6 border border-slate-700">
                <h3 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  More Sharing Options
                </h3>

                {/* NFC Writing - HIDDEN (Admin Only Feature) */}
                {/* This feature is for admin use only - to write customer contacts to NFC cards before shipping */}
                
                <div className="space-y-3">
                  <Button
                    onClick={handleShareMeetingLink}
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white rounded-xl"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Copy Schedule Link
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Posts & Activity - Right Column */}
          <div className="lg:col-span-2">
            <Card className="p-6 bg-slate-800/50 backdrop-blur-sm shadow-2xl border border-slate-700">
              <h2 className="text-2xl font-bold text-white mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Community Posts
              </h2>
              
              {posts.length > 0 ? (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div 
                      key={post.id} 
                      className="p-4 bg-slate-700/30 rounded-xl border border-slate-600 hover:border-cyan-500 transition-colors cursor-pointer"
                      onClick={() => navigate('/community')}
                    >
                      <p className="text-slate-200 mb-2">{post.content}</p>
                      <div className="flex items-center justify-between text-sm text-slate-400">
                        <span>{dayjs(post.createdAt).fromNow()}</span>
                        <div className="flex items-center gap-4">
                          <span>💬 {post.commentCount || 0}</span>
                          <span>❤️ {post.likeCount || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-400">No posts yet</p>
                </div>
              )}
            </Card>

            {/* Actions for other users */}
            {!isOwnProfile && (
              <Card className="p-6 bg-slate-800/50 backdrop-blur-sm shadow-2xl border border-slate-700 mt-6">
                <h3 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Connect with {user.name?.split(' ')[0]}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button
                    onClick={() => navigate(`/schedule/${userId}`)}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl py-6"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Schedule Meeting
                  </Button>
                  <Button
                    onClick={handleSaveAndDownloadVCard}
                    className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-6"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download vCard
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Contact Download Modal for non-logged-in users */}
      <ContactDownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onSubmit={handleModalSubmit}
        contactOwnerName={user?.name || 'this contact'}
      />
    </div>
  );
};

export default UserProfile;
